import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { StatusDot } from '@/components/Page';
import { API_URL } from '@/contexts/AuthContext';

// Tudo o que a Pagar.me devolve sobre o pagamento do pedido (consulta ao vivo, já formatada no backend),
// para o financeiro conciliar: cartão, transação, antifraude, cliente, boleto/PIX e tentativas recusadas.

// Campos que respondem "pagou? quanto? como?" ficam no resumo; o resto é detalhe técnico.
const RESUMO = ['Cobrança', 'Cartão'];

// Grupos de detalhe: título + pares rótulo/valor, em colunas que cabem na largura.
const Secoes = ({ secoes }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-x-8 gap-y-5">
    {secoes.map((s) => (
      <div key={s.titulo} className="min-w-0">
        <p className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-gray-500">{s.titulo}</p>
        <dl className="grid grid-cols-[minmax(96px,auto)_minmax(0,1fr)] gap-x-3 text-[12.5px]">
          {s.campos.map(([rotulo, valor], i) => (
            <Fragment key={`${rotulo}-${i}`}>
              <dt className="truncate py-[3px] text-gray-500">{rotulo}</dt>
              <dd className="min-w-0 py-[3px] font-medium [overflow-wrap:anywhere]">{valor}</dd>
            </Fragment>
          ))}
        </dl>
      </div>
    ))}
  </div>
);

const campo = (secoes, secao, rotulo) =>
  (secoes.find((s) => s.titulo === secao)?.campos || []).find(([r]) => r === rotulo)?.[1];

// Uma cobrança: resumo sempre visível (quem age, valor, cartão, quando) e os
// detalhes técnicos escondidos até o financeiro precisar deles.
const Cobranca = ({ c, aberta }) => {
  const [open, setOpen] = useState(aberta);
  const tone = c.paga ? 'ok' : c.ativa ? 'att' : 'bad';
  const titular = campo(c.secoes, 'Cartão', 'Titular');
  const tipo = campo(c.secoes, 'Cartão', 'Tipo');
  const autorizacao = campo(c.secoes, 'Transação', 'Código de autorização');
  const antifraude = campo(c.secoes, 'Antifraude', 'Antifraude');
  const resumo = c.secoes.filter((s) => RESUMO.includes(s.titulo));
  const detalhes = c.secoes.filter((s) => !RESUMO.includes(s.titulo));
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-sheet2">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 p-4">
        <div className="min-w-0">
          <StatusDot tone={tone} className="text-[12.5px]">{c.status_label}{c.situacao ? `, ${c.situacao.toLowerCase()}` : ''}</StatusDot>
          <p className="mt-1.5 text-[15px] font-medium leading-snug">{c.titulo}</p>
          <p className="mt-0.5 text-[12.5px] text-gray-500">
            {[titular, tipo && tipo.toLowerCase(), c.em].filter(Boolean).join(' · ')}
          </p>
          {c.motivo && <p className="mt-1.5 text-[13px] text-red-700 dark:text-red-300">{c.motivo}</p>}
        </div>
        <div className="text-right">
          <p className="text-[22px] font-medium leading-none tracking-[-0.02em] tabular-nums">{c.valor}</p>
          <p className="mt-1.5 text-[12px] text-gray-500">
            {[autorizacao && `aut. ${autorizacao}`, antifraude && `antifraude ${antifraude.toLowerCase()}`].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-gray-600 transition-colors hover:text-foreground dark:text-gray-400"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
          {open ? 'Ocultar detalhes' : 'Ver detalhes'}
        </button>
        <a
          href={c.painel_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          Painel da Pagar.me <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {open && (
        <div className="space-y-5 border-t border-border bg-card p-4">
          {resumo.length > 0 && <Secoes secoes={resumo} />}
          {detalhes.length > 0 && (
            <div className={resumo.length > 0 ? 'border-t border-border pt-5' : ''}>
              <Secoes secoes={detalhes} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PagamentoPagarme = ({ pedido }) => {
  const pedidoId = pedido.id;
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [verTentativas, setVerTentativas] = useState(null); // null = automático (aberto só se não houver cobrança)

  useEffect(() => {
    let ativo = true;
    setDados(null);
    setErro(null);
    axios
      .get(`${API_URL}/pedidos/${pedidoId}/pagamento-detalhes`)
      .then((r) => ativo && setDados(r.data))
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (ativo) setErro(typeof detail === 'string' ? detail : 'Não foi possível consultar a Pagar.me');
      });
    return () => {
      ativo = false;
    };
  }, [pedidoId]);

  if (erro) return <p className="text-[13px] text-red-700 dark:text-red-300">{erro}</p>;
  if (!dados) {
    return (
      <p className="flex items-center gap-1.5 text-[13px] text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Consultando a Pagar.me…
      </p>
    );
  }
  if (dados.aviso) {
    return (
      <p className="text-[13px] text-amber-800 dark:text-amber-300">
        {dados.aviso}{' '}
        <a href={dados.painel_url} target="_blank" rel="noopener noreferrer" className="underline">
          Abrir o painel da Pagar.me
        </a>
      </p>
    );
  }
  if (!dados.cobrancas.length && !dados.tentativas.length) {
    // cartão que ainda não foi pago: o cliente não abriu/pagou o link — não é "pago por fora"
    if (String(pedido.forma_pagamento || '').startsWith('cartao') && pedido.payment_status !== 'confirmed') {
      return (
        <p className="text-[13px] text-gray-500">
          Aguardando o pagamento pelo link: o cliente ainda não pagou nem tentou pagar (nenhuma cobrança na Pagar.me).
          Os dados do cartão — bandeira, final, titular, parcelas, NSU, autorização e antifraude — aparecem aqui assim
          que ele pagar ou tentar pagar.
        </p>
      );
    }
    return <p className="text-[13px] text-gray-500">Sem cobrança na Pagar.me (PIX direto na conta ou pagamento por comprovante).</p>;
  }
  const tentativasAbertas = verTentativas ?? dados.cobrancas.length === 0;
  return (
    <div className="space-y-3">
      {dados.cobrancas.map((c, i) => (
        <Cobranca key={`${c.painel_url}-${i}`} c={c} aberta={false} />
      ))}
      {dados.tentativas.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setVerTentativas(!tentativasAbertas)}
            aria-expanded={tentativasAbertas}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-gray-600 transition-colors hover:text-foreground dark:text-gray-400"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${tentativasAbertas ? 'rotate-180' : ''}`} />
            {dados.tentativas.length === 1 ? '1 tentativa que não pagou' : `${dados.tentativas.length} tentativas que não pagaram`}
          </button>
          {tentativasAbertas && (
            <div className="mt-3 space-y-3">
              {dados.tentativas.map((c, i) => (
                <Cobranca key={`${c.painel_url}-t${i}`} c={c} aberta={false} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PagamentoPagarme;
